import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import dayjs from 'dayjs'
import { makeStyles } from '@material-ui/core'
import Meeting from './Meeting'

const useStyles = makeStyles(() => ({
  container: {
    backgroundColor: 'transparent'
  },
  meeting: {
    padding: '0.5rem 0rem',
    '& :hover': {
      background: '#F5F6F7'
    }
  },
  // Compact variant — tighter spacing, no hover bg (the compact card has its own bg)
  meetingCompact: {
    padding: '0.25rem 0'
  },
  dayGroup: {
    marginBottom: '1.25rem',
    '&:last-child': {
      marginBottom: 0
    }
  },
  dayHeader: {
    fontSize: '0.875rem',
    color: '#212945',
    marginBottom: '0.5rem'
  },
  dayMeetings: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem'
  },
  divider: {
    margin: '0.625rem 1.5rem'
  }
}))

const MeetingList = ({ meetings, take, linkBaseUrl, variant, groupByDay, showAttendees }) => {
  const classes = useStyles()

  const meetingsPage = useMemo(
    () => meetings?.slice(0, take),
    [meetings, take]
  )

  // When grouping, bucket meetings by day
  const groupedMeetings = useMemo(() => {
    if (!groupByDay) return null

    const groups = {}
    meetingsPage.forEach((meeting) => {
      const dayKey = dayjs(meeting.startDate).format('YYYY-MM-DD')
      const dayLabel = dayjs(meeting.startDate).format('dddd, MMM D')
      if (!groups[dayKey]) {
        groups[dayKey] = { label: dayLabel, meetings: [] }
      }
      groups[dayKey].meetings.push(meeting)
    })
    return Object.entries(groups).map(([key, value]) => ({ key, ...value }))
  }, [meetingsPage, groupByDay])

  // Grouped rendering
  if (groupByDay && groupedMeetings) {
    return (
      <div className={classes.container}>
        {groupedMeetings.map((group) => (
          <div key={group.key} className={classes.dayGroup}>
            <div className={classes.dayHeader}>{group.label}</div>
            <div className={classes.dayMeetings}>
              {group.meetings.map((meeting) => (
                <Meeting
                  key={meeting.meetingId}
                  meeting={meeting}
                  linkBaseUrl={linkBaseUrl}
                  variant={variant}
                  showAttendees={showAttendees}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    )
  }

  // Flat rendering (default, preserves original behavior)
  return (
    <div className={classes.container}>
      {meetingsPage.map((meeting) => (
        <div
          key={meeting.meetingId}
          className={variant === 'compact' ? classes.meetingCompact : classes.meeting}
        >
          <Meeting
            meeting={meeting}
            linkBaseUrl={linkBaseUrl}
            variant={variant}
            showAttendees={showAttendees}
          />
        </div>
      ))}
    </div>
  )
}

MeetingList.propTypes = {
  meetings: PropTypes.array,
  take: PropTypes.number,
  linkBaseUrl: PropTypes.string,
  variant: PropTypes.oneOf(['default', 'compact']),
  groupByDay: PropTypes.bool,
  showAttendees: PropTypes.bool
}

MeetingList.defaultProps = {
  take: 5,
  linkBaseUrl: null,
  variant: 'default',
  groupByDay: false,
  showAttendees: false
}

export default MeetingList
